import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import ProductCard from "../../components/RudrakshaSection/ProductCard";
import Loader from "../../components/Loader/Loader";
import { getProducts } from "../../api/getProducts";

import "../CategoryProducts/CategoryProducts.css";
import "./SearchResults.css";

const PAGE_SIZE = 12;

// Values map 1:1 onto the backend's `sortBy` query param
const SORT_OPTIONS = [
  { value: "", label: "Newest" },
  { value: "priceAsc", label: "Price: Low to High" },
  { value: "priceDesc", label: "Price: High to Low" },
  { value: "nameAsc", label: "Name: A to Z" },
];

/**
 * /search?q=...&page=...&sort=... — full, paginated product search across
 * the whole catalogue. All state lives in the URL so results are
 * shareable and the back button works.
 */
export default function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = (searchParams.get("q") || "").trim();
  const sortBy = searchParams.get("sort") || "";
  const page = Math.max(parseInt(searchParams.get("page"), 10) || 1, 1);

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!query) {
      setProducts([]);
      setPagination(null);
      return undefined;
    }

    const controller = new AbortController();

    setLoading(true);
    setError("");

    getProducts({
      search: query,
      sortBy,
      page,
      limit: PAGE_SIZE,
      signal: controller.signal,
    })
      .then((result) => {
        if (!result?.success) {
          throw new Error(result?.message || "Unable to search products");
        }
        setProducts(result.data?.products || []);
        setPagination(result.data?.pagination || null);
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message || "Unable to search products");
        setProducts([]);
        setPagination(null);
        setLoading(false);
      });

    return () => controller.abort();
  }, [query, sortBy, page]);

  useEffect(() => {
    document.title = query ? `Search: ${query}` : "Search";
  }, [query]);

  const updateParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    setSearchParams(next);
  };

  const goToPage = (nextPage) => {
    updateParams({ page: nextPage > 1 ? nextPage : "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const total = pagination?.total ?? 0;

  return (
    <>
      <Header />

      <section className="category-banner">
        <h1 className="astroBannerHeading">Search</h1>
      </section>

      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span> / </span>
        <span>Search</span>
      </div>

      <section className="category-products search-page">
        {!query ? (
          <div className="search-page-empty">
            <h2>What are you looking for?</h2>
            <p>
              Use the search box above to find Rudraksha, gemstones, crystals
              and more.
            </p>
          </div>
        ) : (
          <>
            <div className="search-page-toolbar">
              <p className="search-page-summary" aria-live="polite">
                {loading && !pagination ? (
                  "Searching…"
                ) : (
                  <>
                    {total} {total === 1 ? "result" : "results"} for{" "}
                    <strong>“{query}”</strong>
                  </>
                )}
              </p>

              {total > 0 && (
                <label className="search-page-sort">
                  <span>Sort by</span>
                  <select
                    value={sortBy}
                    onChange={(e) => updateParams({ sort: e.target.value, page: "" })}
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {loading && products.length === 0 && (
              <div className="products-loading">
                <Loader label="Searching products..." />
              </div>
            )}

            {!loading && error && (
              <div className="search-page-empty">
                <h2>Something went wrong</h2>
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && products.length === 0 && (
              <div className="search-page-empty">
                <h2>No products found</h2>
                <p>
                  Try a different spelling, a shorter word (e.g. “rudraksh”),
                  or browse <Link to="/shop/rudrakshas">Rudrakshas</Link>.
                </p>
              </div>
            )}

            {products.length > 0 && (
              <div className={`products-grid ${loading ? "is-loading" : ""}`}>
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {pagination && pagination.pages > 1 && (
              <nav className="search-page-pagination" aria-label="Search results pages">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => goToPage(page - 1)}
                >
                  Prev
                </button>
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  type="button"
                  disabled={page >= pagination.pages || loading}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </section>

      <Footer />
    </>
  );
}
